import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { validateDriver, addDriver as addDriverService } from './addDriverService';
import { rootUrl } from '../../../config/config';

const SectionTitle = ({ children }) => (
    <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">{children}</h2>
);

const FieldLabel = ({ children, required }) => (
    <label className="block text-sm font-medium text-gray-700 mb-1">
        {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
);

const inputCls = (hasError) =>
    `w-full px-3 py-2 border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary ${hasError ? 'border-red-400' : 'border-gray-300'}`;

const DriverFormPage = () => {
    const { driverId } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const authToken = token || localStorage.getItem('authToken');
    const isEditing = Boolean(driverId);

    const [formData, setFormData] = useState({
        name: '', phone: '', id_no: '', license_no: '',
        license_expiry: '', referred_by: '', status: '', drive_link: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (isEditing) fetchDriver();
    }, [driverId]); // eslint-disable-line

    useEffect(() => {
        axios.get(`${rootUrl}/drivers/statuses?active=true`, {
            headers: { Authorization: `Bearer ${authToken}` },
        }).then(res => {
            const rows = res.data?.data || [];
            setStatusOptions(rows.map(s => ({ value: s.code, label: s.name })));
        }).catch(() => {});
    }, [authToken]); // eslint-disable-line

    const fetchDriver = async () => {
        setFetching(true);
        try {
            const res = await axios.get(`${rootUrl}/drivers/${driverId}`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const d = res.data?.data || res.data;
            setFormData({
                name: d.name || '',
                phone: d.phone || '',
                id_no: d.id_no || '',
                license_no: d.license_no || '',
                license_expiry: d.license_expiry ? String(d.license_expiry).split('T')[0] : '',
                referred_by: d.referred_by || '',
                status: d.status || '',
                drive_link: d.drive_link || '',
            });
        } catch (err) {
            console.error('Error fetching driver:', err);
            navigate('/drivers');
        } finally {
            setFetching(false);
        }
    };

    const set = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors(prev => { const e = { ...prev }; delete e[key]; return e; });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        const validationErrors = validateDriver(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            if (isEditing) {
                const { drive_link: _dl, ...editPayload } = formData;
                await axios.put(`${rootUrl}/drivers/${driverId}`, {
                    ...editPayload,
                    license_expiry: formData.license_expiry,
                    edit_by: user?.username || '',
                    edit_date: new Date().toISOString().split('T')[0],
                }, { headers: { Authorization: `Bearer ${authToken}` } });
            } else {
                const apiCall = async (method, endpoint, data) => axios({
                    method,
                    url: `${rootUrl}${endpoint}`,
                    headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                    data,
                });
                const result = await addDriverService(apiCall, formData, user);
                if (!result.success) {
                    setSubmitError(result.error || 'Error adding driver');
                    setLoading(false);
                    return;
                }
            }
            navigate('/drivers');
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Error saving driver';
            setSubmitError(msg);
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="p-6 min-h-screen bg-brand-gradient flex items-center justify-center">
                <p className="text-sm text-gray-400">Loading driver...</p>
            </div>
        );
    }


    return (
        <div className="p-4 md:p-6 min-h-screen bg-brand-gradient">
            <div className="flex items-center gap-3 mb-6">
                <button
                    type="button"
                    onClick={() => navigate('/drivers')}
                    className="h-8 w-8 flex items-center justify-center rounded-sm border border-gray-300 hover:bg-gray-50 text-gray-500 transition-colors flex-shrink-0"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                </button>
                <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-brand-primary">Drivers</p>
                    <h1 className="text-xl font-bold text-gray-900 leading-tight">
                        {isEditing ? 'Edit Driver' : 'New Driver'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-3xl">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="p-5">
                        <SectionTitle>Driver Information</SectionTitle>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <FieldLabel required>Driver Name</FieldLabel>
                                <input
                                    value={formData.name}
                                    onChange={e => set('name', e.target.value.toUpperCase())}
                                    className={inputCls(errors.name)}
                                    placeholder="FULL NAME"
                                />
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>
                            <div>
                                <FieldLabel required>Phone</FieldLabel>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={e => { if (/^\d{0,10}$/.test(e.target.value)) set('phone', e.target.value); }}
                                    className={inputCls(errors.phone)}
                                    placeholder="0712345678"
                                />
                                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                            </div>
                            <div>
                                <FieldLabel required>ID Number</FieldLabel>
                                <input
                                    value={formData.id_no}
                                    onChange={e => set('id_no', e.target.value)}
                                    className={inputCls(errors.id_no)}
                                    placeholder="National ID"
                                />
                                {errors.id_no && <p className="text-xs text-red-500 mt-1">{errors.id_no}</p>}
                            </div>
                            <div>
                                <FieldLabel>License Number</FieldLabel>
                                <input
                                    value={formData.license_no}
                                    onChange={e => set('license_no', e.target.value)}
                                    className={inputCls(errors.license_no)}
                                    placeholder="DL123456"
                                />
                                {errors.license_no && <p className="text-xs text-red-500 mt-1">{errors.license_no}</p>}
                            </div>
                            <div>
                                <FieldLabel required>License Expiry</FieldLabel>
                                <input
                                    type="date"
                                    value={formData.license_expiry}
                                    onChange={e => set('license_expiry', e.target.value)}
                                    className={inputCls(errors.license_expiry)}
                                />
                                {errors.license_expiry && <p className="text-xs text-red-500 mt-1">{errors.license_expiry}</p>}
                            </div>
                            <div>
                                <FieldLabel>Referred By</FieldLabel>
                                <input
                                    value={formData.referred_by}
                                    onChange={e => set('referred_by', e.target.value)}
                                    className={inputCls(errors.referred_by)}
                                    placeholder="Referral name"
                                />
                                {errors.referred_by && <p className="text-xs text-red-500 mt-1">{errors.referred_by}</p>}
                            </div>
                            <div>
                                <FieldLabel required>Status</FieldLabel>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(o => o.value === formData.status) || null}
                                    onChange={opt => set('status', opt?.value || '')}
                                    placeholder="Select status..."
                                    isClearable
                                />
                                {errors.status && <p className="text-xs text-red-500 mt-1">{errors.status}</p>}
                            </div>
                            <div>
                                <FieldLabel>Drive Folder</FieldLabel>
                                <div className="w-full border border-gray-300 rounded-sm px-3 py-2 bg-gray-50 text-sm text-gray-600">
                                    {formData.drive_link ? (
                                        <a href={formData.drive_link} target="_blank" rel="noreferrer noopener" className="text-blue-600 hover:underline break-all">
                                            Open auto-generated Drive folder
                                        </a>
                                    ) : (
                                        <span className="text-gray-400">Auto-generated after save</span>
                                    )}
                                </div>
                                <p className="mt-1 text-xs text-gray-400">This folder is created automatically and cannot be edited manually.</p>
                            </div>
                        </div>
                    </div>

                    {submitError && (
                        <div className="px-5 pb-3">
                            <p className="text-sm text-red-500 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{submitError}</p>
                        </div>
                    )}

                    <div className="border-t border-gray-100 px-5 py-4 flex flex-col sm:flex-row justify-end gap-2 bg-gray-50/50">
                        <button
                            type="button"
                            onClick={() => navigate('/drivers')}
                            className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-medium rounded-sm transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold rounded-sm disabled:opacity-50 transition-colors"
                        >
                            <FontAwesomeIcon icon={faSave} />
                            {loading ? 'Saving...' : isEditing ? 'Update Driver' : 'Save Driver'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default DriverFormPage;
